import React from "react";
import "./widget-cards.css";

import type { T_ChatV8RenderableItem } from "@lattice/shared-frontend/chat-v8";
import {
  getPlacedWidgetTypeLabel,
  parsePlacedWidget,
} from "@lattice/shared-frontend/chat-v8";

import { ChartWidgetCard } from "./ChartWidgetCard";
import { FeedWidgetCard } from "./FeedWidgetCard";
import { InsightWidgetCard } from "./InsightWidgetCard";
import { PortfolioRatingCard } from "./PortfolioRatingCard";
import { StockWidgetCard } from "./StockWidgetCard";
import { UpcomingEventsCard } from "./UpcomingEventsCard";
import { TableWidgetCard } from "./TableWidgetCard";

export type T_PlacedWidgetItemProps = {
  item: T_ChatV8RenderableItem;
};

/**
 * Web mirror of the mobile PlacedWidgetItem: dispatches a renderable
 * widget message part through the shared parsePlacedWidget projection.
 * Unknown or unavailable widgets degrade to labeled cards — never crash,
 * never blank.
 */
export function PlacedWidgetItem({ item }: T_PlacedWidgetItemProps) {
  const result = parsePlacedWidget(item);

  switch (result.kind) {
    case "loading":
      return (
        <WidgetLoadingCard
          state={result.state}
          widgetType={result.widgetType}
        />
      );
    case "unavailable":
      return (
        <WidgetUnavailableCard state={result.state} title={result.title} />
      );
    case "stock":
      return (
        <PlacedWidgetFrame>
          <StockWidgetCard data={result.data} isStreaming={item.isLive} />
        </PlacedWidgetFrame>
      );
    case "chart":
      return (
        <PlacedWidgetFrame>
          <ChartWidgetCard data={result.data} />
        </PlacedWidgetFrame>
      );
    case "table":
      return (
        <PlacedWidgetFrame>
          <TableWidgetCard data={result.data} />
        </PlacedWidgetFrame>
      );
    case "insight":
      return (
        <PlacedWidgetFrame>
          <InsightWidgetCard data={result.data} />
        </PlacedWidgetFrame>
      );
    case "portfolio_rating":
      return (
        <PlacedWidgetFrame>
          <PortfolioRatingCard data={result.data} />
        </PlacedWidgetFrame>
      );
    case "upcoming_events":
      return (
        <PlacedWidgetFrame>
          <UpcomingEventsCard data={result.data} />
        </PlacedWidgetFrame>
      );
    case "feed":
      return (
        <PlacedWidgetFrame>
          <FeedWidgetCard data={result.data} />
        </PlacedWidgetFrame>
      );
    case "unknown":
      return <FallbackCard label="This widget couldn't be loaded." />;
  }

  const exhaustiveKind: never = result;
  return exhaustiveKind;
}

function PlacedWidgetFrame({ children }: { children: React.ReactNode }) {
  // RAI-1094: no partial-data note. "Partial data · N warnings" was the only
  // degraded-data signal, but it named neither the missing data nor a remedy,
  // so it read as noise. If degraded-data signaling returns, it must say WHAT
  // is missing, not count anonymous warnings.
  return <div className="cw-plain">{children}</div>;
}

function WidgetLoadingCard({
  state,
  widgetType,
}: {
  state: string;
  widgetType: string;
}) {
  const label = getPlacedWidgetTypeLabel(widgetType).toLowerCase();
  const message =
    state === "completed"
      ? `Loading ${label} widget…`
      : `Preparing ${label} widget…`;
  return (
    <div className="cw-card cw-status" role="status">
      <span aria-hidden className="cw-spinner" />
      <p className="cw-muted">{message}</p>
    </div>
  );
}

function WidgetUnavailableCard({
  state,
  title,
}: {
  state: string;
  title: string;
}) {
  return (
    <div className="cw-card">
      <p className="cw-title">{title}</p>
      <p className="cw-muted">
        {state === "failed" || state === "completed"
          ? `This widget ${state === "failed" ? "failed" : "data is unavailable"}.`
          : "This widget data is unavailable."}
      </p>
    </div>
  );
}

function FallbackCard({ label }: { label: string }) {
  return (
    <div className="cw-card">
      <p className="cw-muted">{label}</p>
    </div>
  );
}
